import React, { useEffect, useMemo, useRef, useState } from "react"
import axios from 'axios'
import '../Dashboard.css'
import '../styles/Sedes.css'

import Header from "../components/Header"
import Footer from "../components/Footer"
import ScrollToTop from "../components/ScrollTop"
import PhoneLoader from "../components/loader/PhoneLoader"

import { ImCross, ImEye, ImSearch } from "react-icons/im"
import { useAuth } from '../provider/authProvider'
import { toast } from 'react-toastify'

import {
    handleCoorsInput,
    handleCustomInput
} from "../utils/inputUtils";

const EMPTY_FORM = {
    sedeName: '',
    sedeLocation: '',
    latitude: '',
    longitude: '',
    state: '1',
    departmentCode: '',
    provinceCode: '',
    ubigeo: '',
    branchCode: '',
    establishmentType: 'STORE',
}

function UserSedes(props) {
    const { user } = useAuth()

    const [listSedes, setListSedes] = useState([])
    const [loading, setLoading] = useState(false)
    const [showPopupAdd, setShowPopupAdd] = useState(false)
    const [showPopupEdit, setShowPopupEdit] = useState(false)
    const [idForEdit, setIdForEdit] = useState(null)
    const [arrStates, setArrStates] = useState([])

    const [departments, setDepartments] = useState([])
    const [provinces, setProvinces] = useState([])
    const [districts, setDistricts] = useState([])
    const [form, setForm] = useState(EMPTY_FORM)
    const [searchTerm, setSearchTerm] = useState('')
    const [showGreRucModal, setShowGreRucModal] = useState(false)
    const [greRucLoading, setGreRucLoading] = useState(false)
    const [greRucSaving, setGreRucSaving] = useState(false)
    const [greRucData, setGreRucData] = useState({ sede: null, rucs: [] })

    const [numPages, setNumPages] = useState(0)
    const [selectPagination, setSelectPagination] = useState(1)
    const [totalRecords, setTotalRecords] = useState(0)

    const sedeNameRef = useRef()
    const sedeLocationRef = useRef()
    const latitudeRef = useRef()
    const longitudeRef = useRef()

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        'Content-Type': 'application/json',
    })

    const showError = (error, fallback = 'Ocurrió un error') => {
        toast.error(
            error?.response?.data?.error ||
            error?.response?.data?.msg ||
            fallback
        )
    }

    const getDepartments = async () => {
        try {
            const response = await axios({
                method: 'get',
                url: process.env.REACT_APP_API + '/ubigeo/departments',
                headers: authHeaders(),
            })
            setDepartments(response.data?.data || [])
        } catch (error) {
            showError(error, 'No se pudo cargar los departamentos')
        }
    }

    const getProvinces = async (departmentCode) => {
        if (!departmentCode) {
            setProvinces([])
            return []
        }

        try {
            const response = await axios({
                method: 'get',
                url: process.env.REACT_APP_API + '/ubigeo/provinces/' + departmentCode,
                headers: authHeaders(),
            })
            const data = response.data?.data || []
            setProvinces(data)
            return data
        } catch (error) {
            setProvinces([])
            showError(error, 'No se pudo cargar las provincias')
            return []
        }
    }

    const getDistricts = async (provinceCode) => {
        if (!provinceCode) {
            setDistricts([])
            return []
        }

        try {
            const response = await axios({
                method: 'get',
                url: process.env.REACT_APP_API + '/ubigeo/districts/' + provinceCode,
                headers: authHeaders(),
            })
            const data = response.data?.data || []
            setDistricts(data)
            return data
        } catch (error) {
            setDistricts([])
            showError(error, 'No se pudo cargar los distritos')
            return []
        }
    }

    const getSedes = async (page = selectPagination) => {
        try {
            setLoading(true)
            const response = await axios({
                method: 'get',
                url: process.env.REACT_APP_API + `/sedes?page=${page}&limit=20`,
                headers: authHeaders(),
            })

            const resData = response.data
            setListSedes(resData.data || [])
            setNumPages(resData.numPages || 0)
            setTotalRecords(resData.totalRecords || 0)
            setSelectPagination(page)
        } catch (error) {
            showError(error, 'No se pudo cargar las sedes')
        } finally {
            setLoading(false)
        }
    }

    const openGreRucConfiguration = async (sede) => {
        try {
            setGreRucLoading(true)
            setShowGreRucModal(true)
            document.body.style.overflow = 'hidden'
            const response = await axios({
                method: 'get',
                url: process.env.REACT_APP_API + `/sede/${sede.idNumber}/ruc-establishments`,
                headers: authHeaders(),
            })
            setGreRucData(response.data?.data || { sede: null, rucs: [] })
        } catch (error) {
            setShowGreRucModal(false)
            document.body.style.overflow = 'auto'
            showError(error, 'No se pudo cargar la configuración tributaria GRE')
        } finally {
            setGreRucLoading(false)
        }
    }

    const closeGreRucConfiguration = () => {
        if (greRucSaving) return
        setShowGreRucModal(false)
        setGreRucData({ sede: null, rucs: [] })
        document.body.style.overflow = 'auto'
    }

    const changeGreRucCode = (rucId, value) => {
        const code = String(value || '').replace(/\D/g, '').slice(0, 4)
        setGreRucData((current) => ({
            ...current,
            rucs: (current.rucs || []).map((row) => Number(row.id) === Number(rucId)
                ? { ...row, establishmentCode: code }
                : row),
        }))
    }

    const saveGreRucConfiguration = async () => {
        const invalid = (greRucData.rucs || []).find((row) => row.establishmentCode && !/^\d{4}$/.test(row.establishmentCode))
        if (invalid) return toast.error(`Completa los 4 dígitos del código SUNAT para ${invalid.alias || invalid.businessName}.`)
        try {
            setGreRucSaving(true)
            const sedeId = greRucData.sede?.id
            const response = await axios({
                method: 'put',
                url: process.env.REACT_APP_API + `/sede/${sedeId}/ruc-establishments`,
                headers: authHeaders(),
                data: { mappings: (greRucData.rucs || []).map((row) => ({ rucId: row.id, establishmentCode: row.establishmentCode || '' })) },
            })
            setGreRucData(response.data?.data || greRucData)
            toast.success('Configuración tributaria GRE guardada correctamente.')
        } catch (error) {
            showError(error, 'No se pudo guardar la configuración tributaria GRE')
        } finally {
            setGreRucSaving(false)
        }
    }

    const resetForm = () => {
        setForm(EMPTY_FORM)
        setProvinces([])
        setDistricts([])
        setIdForEdit(null)
    }

    const openAddPopup = () => {
        resetForm()
        setShowPopupAdd(true)
        document.body.style.overflow = 'hidden'
    }

    const closePopup = () => {
        setShowPopupAdd(false)
        setShowPopupEdit(false)
        resetForm()
        document.body.style.overflow = 'auto'
    }

    const handleTextChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }))
    }

    const handleDepartmentChange = async (event) => {
        const departmentCode = event.target.value

        setForm((prev) => ({
            ...prev,
            departmentCode,
            provinceCode: '',
            ubigeo: '',
        }))

        setDistricts([])
        await getProvinces(departmentCode)
    }

    const handleProvinceChange = async (event) => {
        const provinceCode = event.target.value

        setForm((prev) => ({
            ...prev,
            provinceCode,
            ubigeo: '',
        }))

        await getDistricts(provinceCode)
    }

    const handleDistrictChange = (event) => {
        setForm((prev) => ({
            ...prev,
            ubigeo: event.target.value,
        }))
    }

    const handleBranchCodeChange = (event) => {
        const value = event.target.value.replace(/\D/g, '').slice(0, 4)
        setForm((prev) => ({ ...prev, branchCode: value }))
    }

    const validateForm = () => {
        if (!form.sedeName.trim()) return 'Ingresa el nombre de la sede.'
        if (!form.departmentCode) return 'Selecciona el departamento.'
        if (!form.provinceCode) return 'Selecciona la provincia.'
        if (!form.ubigeo) return 'Selecciona el distrito.'
        if (!/^\d{4}$/.test(form.branchCode)) return 'El código de establecimiento debe tener exactamente 4 dígitos.'
        if (!['STORE', 'WAREHOUSE', 'OFFICE', 'OTHER'].includes(form.establishmentType)) return 'Selecciona un tipo de establecimiento.'
        if (!form.sedeLocation.trim()) return 'Ingresa la dirección del establecimiento.'
        if (form.latitude === '') return 'Ingresa la latitud.'
        if (form.longitude === '') return 'Ingresa la longitud.'
        return null
    }

    const sendFmData = async () => {
        const validationError = validateForm()
        if (validationError) {
            toast.error(validationError)
            return
        }

        const pathApi = showPopupAdd
            ? process.env.REACT_APP_API + '/sede/add'
            : process.env.REACT_APP_API + '/sede/full-edit'

        const payload = {
            ...form,
            sedeName: form.sedeName.trim(),
            sedeLocation: form.sedeLocation.trim(),
        }

        if (!showPopupAdd) {
            payload.id = idForEdit
        }

        try {
            setLoading(true)

            const response = await axios({
                method: 'POST',
                url: pathApi,
                headers: authHeaders(),
                data: payload,
            })

            const resData = response.data

            if (resData.status === 1 || resData.msg === 'success') {
                toast.success(showPopupAdd ? 'ESTABLECIMIENTO REGISTRADO CON ÉXITO' : 'ESTABLECIMIENTO ACTUALIZADO CON ÉXITO', {
                    position: "top-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored"
                })

                closePopup()
                await getSedes(selectPagination)
            }
        } catch (error) {
            showError(error, 'Error al procesar la sede')
        } finally {
            setLoading(false)
        }
    }

    const getSedeEdit = async (event, id) => {
        event.preventDefault()

        try {
            setLoading(true)

            const response = await axios({
                method: 'get',
                url: process.env.REACT_APP_API + '/get-sede-edit/' + id,
                headers: authHeaders(),
            })

            const sede = response.data?.data
            if (!sede) return

            if (sede.departmentCode) {
                await getProvinces(sede.departmentCode)
            } else {
                setProvinces([])
            }

            if (sede.provinceCode) {
                await getDistricts(sede.provinceCode)
            } else {
                setDistricts([])
            }

            setForm({
                sedeName: sede.sedeName || '',
                sedeLocation: sede.sedeLocation || '',
                latitude: sede.lat ?? '',
                longitude: sede.lng ?? '',
                state: String(sede.sedeState ?? '1'),
                departmentCode: sede.departmentCode || '',
                provinceCode: sede.provinceCode || '',
                ubigeo: sede.ubigeo || '',
                branchCode: sede.branchCode || '',
                establishmentType: sede.establishmentType || 'STORE',
            })

            setIdForEdit(id)
            setShowPopupEdit(true)
            document.body.style.overflow = 'hidden'
        } catch (error) {
            showError(error, 'Error al obtener la sede')
        } finally {
            setLoading(false)
        }
    }

    const getArrStates = (id, state) => ({ id: id || null, state: state ?? null })

    const changeState = (event, idNumber) => {
        const next = getArrStates(idNumber, event.target.value)
        setArrStates((prev) => [...prev, next])
    }

    const saveRols = async (event) => {
        event.preventDefault()

        const mapFromState = new Map(arrStates.map((item) => [item.id, item]))
        const uniqueState = [...mapFromState.values()]

        if (!uniqueState.length) {
            toast.info('No hay cambios de estado pendientes.')
            return
        }

        try {
            setLoading(true)

            const response = await axios({
                method: 'POST',
                url: process.env.REACT_APP_API + '/sede/edit',
                headers: {
                    ...authHeaders(),
                    user,
                },
                data: { states: JSON.stringify(uniqueState) }
            })

            if (response.data?.msg === 'exito') {
                setArrStates([])
                toast.success('Estados actualizados correctamente')
                await getSedes(selectPagination)
            }
        } catch (error) {
            showError(error, 'No se pudo guardar los estados')
        } finally {
            setLoading(false)
        }
    }

    const filteredSedes = useMemo(() => {
        const term = searchTerm.trim().toLowerCase()
        if (!term) return listSedes

        return listSedes.filter((item) => {
            const haystack = [
                item.branchCode,
                item.sede,
                item.establishmentType,
                item.departmentName,
                item.provinceName,
                item.districtName,
                item.ubigeo,
                item.sedeLocation,
            ].filter(Boolean).join(' ').toLowerCase()

            return haystack.includes(term)
        })
    }, [listSedes, searchTerm])

    const activeCount = listSedes.filter((item) => Number(item.sedeState) === 1).length
    const inactiveCount = listSedes.filter((item) => Number(item.sedeState) === 0).length

    useEffect(() => {
        localStorage.removeItem('linkNumber')
        localStorage.removeItem('dniEnroll')
        getDepartments()
        getSedes(1)
    }, [])

    const renderForm = (isEdit) => (
        <div className="sedeModal" role="dialog" aria-modal="true" aria-labelledby="sedeModalTitle">
            <div className="sedeModalHeader">
                <div>
                    <span className="sedeModalEyebrow">Gestión de sedes</span>
                    <h2 id="sedeModalTitle">{isEdit ? 'Editar sede' : 'Registrar nueva sede'}</h2>
                    <p>
                        {isEdit
                            ? 'Actualiza la identificación, ubicación geográfica y coordenadas de la sede.'
                            : 'Completa la información para incorporar una nueva sede al sistema.'}
                    </p>
                </div>
                <button type="button" className="sedeModalClose" onClick={closePopup} aria-label="Cerrar">
                    <ImCross />
                </button>
            </div>

            <div className="sedeModalBody">
                <div className="sedeFormSection">
                    <div className="sedeFormSectionTitle">
                        <strong>Identificación</strong>
                        <span>Datos internos de la sede</span>
                    </div>

                    <div className="sedeFormGrid sedeFormGridThree">
                        <div className="sedeField">
                            <label htmlFor="establishmentType">Tipo <b>*</b></label>
                            <select
                                id="establishmentType"
                                value={form.establishmentType}
                                onChange={(event) => handleTextChange('establishmentType', event.target.value)}
                            >
                                <option value="STORE">Tienda</option>
                                <option value="WAREHOUSE">Almacén</option>
                                <option value="OFFICE">Oficina</option>
                                <option value="OTHER">Otro</option>
                            </select>
                        </div>

                        <div className="sedeField">
                            <label htmlFor="sedeName">Nombre del establecimiento <b>*</b></label>
                            <input
                                id="sedeName"
                                ref={sedeNameRef}
                                value={form.sedeName}
                                onInput={(event) => handleCustomInput(event, 40)}
                                onChange={(event) => handleTextChange('sedeName', event.target.value)}
                                placeholder="Ej. Yarina"
                                autoComplete="off"
                            />
                        </div>

                        <div className="sedeField">
                            <label htmlFor="branchCode">Código de establecimiento <b>*</b></label>
                            <div className="sedeCodeInput">
                                <span>#</span>
                                <input
                                    id="branchCode"
                                    value={form.branchCode}
                                    onChange={handleBranchCodeChange}
                                    inputMode="numeric"
                                    maxLength={4}
                                    placeholder="0001"
                                    autoComplete="off"
                                />
                            </div>
                            <small>Código interno único de 4 dígitos.</small>
                        </div>
                    </div>
                </div>

                <div className="sedeFormSection">
                    <div className="sedeFormSectionTitle">
                        <strong>Ubicación geográfica</strong>
                        <span>Catálogo jerárquico de UBIGEO</span>
                    </div>

                    <div className="sedeFormGrid sedeFormGridThree">
                        <div className="sedeField">
                            <label htmlFor="departmentCode">Departamento <b>*</b></label>
                            <select id="departmentCode" value={form.departmentCode} onChange={handleDepartmentChange}>
                                <option value="">Seleccionar departamento</option>
                                {departments.map((item) => (
                                    <option key={item.code} value={item.code}>{item.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="sedeField">
                            <label htmlFor="provinceCode">Provincia <b>*</b></label>
                            <select
                                id="provinceCode"
                                value={form.provinceCode}
                                onChange={handleProvinceChange}
                                disabled={!form.departmentCode}
                            >
                                <option value="">Seleccionar provincia</option>
                                {provinces.map((item) => (
                                    <option key={item.code} value={item.code}>{item.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="sedeField">
                            <label htmlFor="ubigeo">Distrito <b>*</b></label>
                            <select
                                id="ubigeo"
                                value={form.ubigeo}
                                onChange={handleDistrictChange}
                                disabled={!form.provinceCode}
                            >
                                <option value="">Seleccionar distrito</option>
                                {districts.map((item) => (
                                    <option key={item.code} value={item.code}>{item.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="sedeUbigeoPreview">
                        <span>UBIGEO INEI</span>
                        <strong>{form.ubigeo || '------'}</strong>
                        <small>Se completa automáticamente al seleccionar el distrito.</small>
                    </div>
                </div>

                <div className="sedeFormSection">
                    <div className="sedeFormSectionTitle">
                        <strong>Dirección y georreferencia</strong>
                        <span>Ubicación operativa de la tienda</span>
                    </div>

                    <div className="sedeFormGrid">
                        <div className="sedeField sedeFieldFull">
                            <label htmlFor="sedeLocation">Dirección de sede <b>*</b></label>
                            <input
                                id="sedeLocation"
                                ref={sedeLocationRef}
                                value={form.sedeLocation}
                                onInput={(event) => handleCustomInput(event, 100)}
                                onChange={(event) => handleTextChange('sedeLocation', event.target.value)}
                                placeholder="Ej. Av. Yarinacocha 123, referencia..."
                                autoComplete="off"
                            />
                        </div>
                    </div>

                    <div className="sedeFormGrid sedeFormGridTwo">
                        <div className="sedeField">
                            <label htmlFor="latitude">Latitud <b>*</b></label>
                            <input
                                id="latitude"
                                ref={latitudeRef}
                                value={form.latitude}
                                onInput={handleCoorsInput}
                                onChange={(event) => handleTextChange('latitude', event.target.value)}
                                placeholder="-8.3791"
                                inputMode="decimal"
                            />
                        </div>

                        <div className="sedeField">
                            <label htmlFor="longitude">Longitud <b>*</b></label>
                            <input
                                id="longitude"
                                ref={longitudeRef}
                                value={form.longitude}
                                onInput={handleCoorsInput}
                                onChange={(event) => handleTextChange('longitude', event.target.value)}
                                placeholder="-74.5539"
                                inputMode="decimal"
                            />
                        </div>
                    </div>
                </div>

                <div className="sedeFormSection sedeFormSectionLast">
                    <div className="sedeFormSectionTitle">
                        <strong>Estado</strong>
                        <span>Disponibilidad operativa</span>
                    </div>

                    <div className="sedeField sedeStateField">
                        <select
                            id="stateAdd"
                            value={form.state}
                            onChange={(event) => handleTextChange('state', event.target.value)}
                        >
                            <option value="1">Activo</option>
                            <option value="0">Inactivo</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="sedeModalFooter">
                <button type="button" className="sedeButtonSecondary" onClick={closePopup}>Cancelar</button>
                <button type="button" className="sedeButtonPrimary" onClick={sendFmData}>
                    {isEdit ? 'Guardar cambios' : 'Registrar establecimiento'}
                </button>
            </div>
        </div>
    )

    return (
        <div>
            {loading && <PhoneLoader />}
            <ScrollToTop />
            <Header user={user} namePage={props.namePage} />

            <section className="sectionBody sedePage">
                <div className="containerCustom containerBody">
                    <div className="sedeWorkspace">
                        <div className="sedeHero">
                            <div>
                                <span className="sedeHeroEyebrow">Administración</span>
                                <h1>Sedes y establecimientos</h1>
                                <p>Administra tiendas, almacenes, oficinas y otros establecimientos con dirección, UBIGEO y código operativo único.</p>
                            </div>
                            <button type="button" className="sedeButtonPrimary sedeButtonAdd" onClick={openAddPopup}>
                                <span>+</span> Agregar establecimiento
                            </button>
                        </div>

                        <div className="sedeStats">
                            <div className="sedeStatCard">
                                <span>Total registradas</span>
                                <strong>{totalRecords}</strong>
                            </div>
                            <div className="sedeStatCard">
                                <span>Activas en esta página</span>
                                <strong>{activeCount}</strong>
                            </div>
                            <div className="sedeStatCard">
                                <span>Inactivas en esta página</span>
                                <strong>{inactiveCount}</strong>
                            </div>
                        </div>

                        <div className="sedePanel">
                            <div className="sedePanelHeader">
                                <div>
                                    <h2>Directorio de sedes</h2>
                                    <p>Consulta y administra la información territorial de tus tiendas.</p>
                                </div>

                                <div className="sedeSearchBox">
                                    <ImSearch />
                                    <input
                                        type="search"
                                        value={searchTerm}
                                        onChange={(event) => setSearchTerm(event.target.value)}
                                        placeholder="Buscar establecimiento, tipo, código, distrito o UBIGEO..."
                                    />
                                </div>
                            </div>

                            <div className="sedeTableWrap">
                                <table className="sedeTable">
                                    <thead>
                                        <tr>
                                            <th>Código</th>
                                            <th>Tipo</th>
                                            <th>Establecimiento</th>
                                            <th>Ubicación</th>
                                            <th>UBIGEO</th>
                                            <th>Dirección</th>
                                            <th>Coordenadas</th>
                                            <th>Estado</th>
                                            <th className="sedeTableAction">Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredSedes.length > 0 ? filteredSedes.map((item) => (
                                            <tr key={item.idNumber}>
                                                <td>
                                                    <span className="sedeBranchCode">{item.branchCode || '----'}</span>
                                                </td>
                                                <td>
                                                    <span className={`sedeTypeBadge type-${String(item.establishmentType || 'STORE').toLowerCase()}`}>
                                                        {{
                                                            STORE: 'Tienda',
                                                            WAREHOUSE: 'Almacén',
                                                            OFFICE: 'Oficina',
                                                            OTHER: 'Otro',
                                                        }[item.establishmentType || 'STORE']}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="sedeNameCell">
                                                        <strong>{item.sede}</strong>
                                                        <span>{item.createName ? `Creado por ${item.createName} ${item.createAP || ''}` : 'Sede registrada'}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="sedeLocationCell">
                                                        <strong>{item.districtName || 'Ubicación pendiente'}</strong>
                                                        <span>
                                                            {[item.provinceName, item.departmentName].filter(Boolean).join(' · ') || 'Completar ubicación geográfica'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td><span className="sedeUbigeoTag">{item.ubigeo || '------'}</span></td>
                                                <td className="sedeAddressCell">{item.sedeLocation}</td>
                                                <td>
                                                    <div className="sedeCoordsCell">
                                                        <span>{item.lat}</span>
                                                        <span>{item.lng}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <select
                                                        defaultValue={String(item.sedeState)}
                                                        onChange={(event) => changeState(event, item.idNumber)}
                                                        className={`sedeStatusSelect ${Number(item.sedeState) === 1 ? 'isActive' : 'isInactive'}`}
                                                    >
                                                        <option value="1">Activo</option>
                                                        <option value="0">Inactivo</option>
                                                    </select>
                                                </td>
                                                <td className="sedeTableAction">
                                                    <button type="button" className="sedeGreButton" onClick={() => openGreRucConfiguration(item)} aria-label={`Configurar RUC SUNAT de ${item.sede}`}>SUNAT</button>
                                                    <button
                                                        type="button"
                                                        className="sedeEditButton"
                                                        onClick={(event) => getSedeEdit(event, item.idNumber)}
                                                        aria-label={`Editar ${item.sede}`}
                                                    >
                                                        <ImEye />
                                                    </button>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="9">
                                                    <div className="sedeEmptyState">
                                                        <strong>No se encontraron establecimientos</strong>
                                                        <span>Prueba con otro término de búsqueda o registra un nuevo establecimiento.</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="sedePanelFooter">
                                <div className="sedePagination">
                                    <button
                                        type="button"
                                        disabled={selectPagination <= 1}
                                        onClick={() => getSedes(selectPagination - 1)}
                                    >
                                        Anterior
                                    </button>

                                    <span>Página <strong>{selectPagination}</strong> de <strong>{Math.max(numPages, 1)}</strong></span>

                                    <button
                                        type="button"
                                        disabled={selectPagination >= numPages}
                                        onClick={() => getSedes(selectPagination + 1)}
                                    >
                                        Siguiente
                                    </button>
                                </div>

                                <button
                                    type="button"
                                    className="sedeButtonSecondary sedeSaveStates"
                                    onClick={saveRols}
                                    disabled={!arrStates.length}
                                >
                                    Guardar estados {arrStates.length > 0 ? `(${new Set(arrStates.map((item) => item.id)).size})` : ''}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />

            {showGreRucModal && (
                <div className="sedeModalOverlay sedeGreOverlay">
                    <div className="sedeModalBackdrop" onClick={closeGreRucConfiguration}></div>
                    <section className="sedeGreModal" role="dialog" aria-modal="true" aria-labelledby="gre-ruc-title">
                        <header className="sedeGreHeader">
                            <div><span>FUENTE MAESTRA GRE</span><h2 id="gre-ruc-title">RUC por establecimiento</h2><p>{greRucData.sede?.name || 'Cargando establecimiento...'}</p></div>
                            <button type="button" onClick={closeGreRucConfiguration} aria-label="Cerrar">×</button>
                        </header>
                        <div className="sedeGreBody">
                            <div className="sedeGreLocation">
                                <div><small>DIRECCIÓN</small><strong>{greRucData.sede?.address || '—'}</strong></div>
                                <div><small>UBIGEO</small><strong>{greRucData.sede?.ubigeo || '------'}</strong></div>
                            </div>
                            <div className="sedeGreInfo"><strong>Configuración tributaria logística</strong><span>Cada RUC puede tener un código SUNAT distinto en esta misma ubicación. Las nuevas GRE tomarán estos valores automáticamente.</span></div>
                            {greRucLoading ? <div className="sedeGreLoading">Cargando RUC...</div> : (
                                <div className="sedeGreRucList">
                                    {(greRucData.rucs || []).map((row) => {
                                        const valid = /^\d{4}$/.test(String(row.establishmentCode || ''))
                                        return <article key={row.id} className={valid ? 'is-ready' : 'is-pending'}>
                                            <div className="sedeGreRucIdentity"><strong>{row.alias || row.businessName}</strong><span>{row.businessName}</span><code>{row.ruc}</code></div>
                                            <label><span>Código establecimiento SUNAT</span><input inputMode="numeric" maxLength={4} value={row.establishmentCode || ''} onChange={(e) => changeGreRucCode(row.id, e.target.value)} placeholder="0000" /><small>{valid ? 'Configurado para GRE' : 'Pendiente de configurar'}</small></label>
                                        </article>
                                    })}
                                </div>
                            )}
                        </div>
                        <footer className="sedeGreFooter"><button type="button" className="sedeButtonSecondary" onClick={closeGreRucConfiguration}>Cancelar</button><button type="button" className="sedeButtonPrimary" disabled={greRucSaving || greRucLoading} onClick={saveGreRucConfiguration}>{greRucSaving ? 'Guardando...' : 'Guardar configuración GRE'}</button></footer>
                    </section>
                </div>
            )}

            {(showPopupAdd || showPopupEdit) && (
                <div className="sedeModalOverlay">
                    <div className="sedeModalBackdrop" onClick={closePopup}></div>
                    {renderForm(showPopupEdit)}
                </div>
            )}

        </div>
    )
}

export default UserSedes